/**
 * Promise based wrapper over the vendored DYMO Connect SDK
 * (`public/static/dymo.connect.framework.js`, exposed as
 * `window.dymo.label.framework`).
 *
 * The SDK's default calls are synchronous XHRs to the local DYMO Connect web
 * service and throw synchronously. Everything here sits inside an async
 * function so a throw becomes a rejection, and callers get one failure shape.
 *
 * `listPrinters` keeps the one distinction the dialogs need: a failed request
 * (service not running) rejects, while a service that answered with no
 * printers resolves to an empty list.
 *
 * Printing goes through `printLabelSet`, which talks to the web service
 * directly; see its comment for why.
 *
 * Never call `DYMO.init()`: it starts async service discovery and concurrent
 * calls throw while it runs.
 */

declare global {
  interface Window {
    dymo: any;
  }
}

export type DymoPrinter = {
  name: string;
  modelName: string;
  isConnected: boolean;
  printerType: string;
};

export type PrinterType = "LabelWriterPrinter" | "TapePrinter" | string;

// Not `["dymo:printers:list"]`: the legacy `PrinterSelectPopup.js` caches the
// SDK's sync printer list under that key, which reads a stopped service as no
// printers.
export const DYMO_PRINTERS_QUERY_KEY = ["dymo:printers:async"];

const framework = () => {
  const fw = window.dymo?.label?.framework;
  if (!fw) {
    throw new Error("The DYMO Connect framework is not loaded on this page.");
  }
  return fw;
};

/**
 * The printers DYMO Connect knows about. Rejects when the service doesn't
 * answer; resolves `[]` when it answered with no printers.
 */
export async function listPrinters(): Promise<DymoPrinter[]> {
  const fw = framework();
  const collection = await fw.getPrintersAsync();
  // The SDK returns an array that also carries name keyed properties; keep
  // plain objects so React Query's structural sharing has nothing to strip.
  return Array.from(collection || []).map((p: any) => ({
    name: p.name,
    modelName: p.modelName,
    isConnected: !!p.isConnected,
    printerType: p.printerType,
  }));
}

/** A copies count as a whole number, at least 1. */
export const normalizeCopies = (copies: unknown): number =>
  Math.max(1, Math.floor(Number(copies) || 1));

/**
 * Print params for one job. Returns "" for a single copy because the SDK omits
 * `<Copies>` when the value is falsy anyway, and an empty params string is the
 * SDK's own default.
 */
export function buildPrintParamsXml({
  copies,
  printerType,
}: {
  copies: number;
  printerType: PrinterType;
}): string {
  const count = normalizeCopies(copies);
  if (count === 1) return "";
  const fw = framework();
  return printerType === "LabelWriterPrinter"
    ? fw.createLabelWriterPrintParamsXml({ copies: count })
    : fw.createTapePrintParamsXml({ copies: count });
}

/**
 * Where the SDK found DYMO Connect. Discovery (any SDK call, like
 * `listPrinters`) stores the host and port in localStorage; fall back to the
 * default port when it hasn't run yet.
 */
const serviceBaseUrl = () => {
  let host = "127.0.0.1";
  let port = "41951";
  try {
    host = window.localStorage.getItem("ServiceHost") || host;
    port = window.localStorage.getItem("ServicePort") || port;
  } catch {
    // Storage blocked: use the defaults.
  }
  return `https://${host}:${port}/DYMO/DLS/Printing`;
};

/**
 * How long to wait for DYMO Connect to accept a batch of `itemCount` records.
 * Hand off takes about 17 ms per item and barely moves with copies, so the
 * timeout scales with items (ENG-2071).
 */
export const batchTimeoutMs = (itemCount: number) => 15000 + 40 * itemCount;

export type BatchResult = "accepted" | "rejected" | "unknown";

export class DymoBatchError extends Error {
  result: Exclude<BatchResult, "accepted">;
  constructor(result: Exclude<BatchResult, "accepted">, message: string) {
    super(message);
    this.name = "DymoBatchError";
    this.result = result;
  }
}

/**
 * Hands a whole batch to DYMO Connect as one job: the template plus a label
 * set with one record per item. One job, because DYMO drops a job that
 * arrives while the printer is still busy with the previous one.
 *
 * Calls the web service directly instead of the SDK's `printLabelAsync`, which
 * gives up after 10 seconds while DYMO Connect keeps going (a big batch takes
 * longer than that to hand off). Rejects with a `DymoBatchError`:
 * `rejected` when DYMO said no or couldn't be reached (nothing was queued),
 * `unknown` when we stopped waiting or lost a 2xx answer's body (the batch may
 * still be printing, so never retry automatically).
 */
export async function printLabelSet({
  labelXml,
  labelSetXml,
  printerName,
  paramsXml,
  timeoutMs,
}: {
  labelXml: string;
  labelSetXml: string;
  printerName: string;
  paramsXml: string;
  timeoutMs: number;
}): Promise<void> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let ok = false;
  let body = "";
  try {
    const response = await fetch(`${serviceBaseUrl()}/PrintLabel`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        printerName,
        printParamsXml: paramsXml,
        labelXml,
        labelSetXml,
      }).toString(),
      signal: controller.signal,
    });
    ok = response.ok;
    // Still inside the timeout: a body that never finishes is no answer too.
    body = (await response.text()).trim();
  } catch {
    if (controller.signal.aborted) {
      throw new DymoBatchError("unknown", "DYMO Connect didn't answer in time");
    }
    // A 2xx means DYMO Connect already took the batch, so losing the body
    // after that can't rule out that it's printing.
    if (ok) {
      throw new DymoBatchError("unknown", "DYMO Connect's answer got cut off");
    }
    throw new DymoBatchError("rejected", "Couldn't reach DYMO Connect");
  } finally {
    clearTimeout(timer);
  }
  if (!ok || body === "false") {
    throw new DymoBatchError(
      "rejected",
      body && body !== "false" ? body : "DYMO Connect didn't accept the batch"
    );
  }
}

/**
 * A PNG data URL of one label, rendered by DYMO Connect. By default DYMO
 * renders at screen resolution, which is right for a small preview but too
 * coarse to zoom into; `printerResolution` renders at the printer's.
 */
export async function renderLabelPreview({
  labelXml,
  printerName,
  printerResolution = false,
}: {
  labelXml: string;
  printerName?: string;
  printerResolution?: boolean;
}): Promise<string> {
  const fw = framework();
  const renderParams = printerResolution
    ? fw.createLabelRenderParamsXml({ pngUseDisplayResolution: false })
    : "";
  const png = await fw.renderLabelAsync(
    labelXml,
    renderParams,
    printerName || ""
  );
  if (!png || typeof png !== "string") {
    throw new Error("DYMO Connect didn't return a preview");
  }
  return `data:image/png;base64,${png}`;
}
